import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    DialogTitle,
    IconButton,
    Avatar,
    Box,
    Typography,
    Collapse,
    Button,
    Popover,
    Tooltip,
    LinearProgress,
    Fade,
    Chip,
    Divider,
} from "@mui/material";
import SettingsDialog from "./SettingsDialog";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import Groups2Icon from "@mui/icons-material/Groups2";
import ImageIcon from "@mui/icons-material/Image";
import BoltIcon from "@mui/icons-material/Bolt";
import SearchIcon from "@mui/icons-material/Search";
import PowerPointIcon from "./PowerPointIcon";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import { sidebarTranslations } from "./Translations/Sidebar.js";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LimitDialog from "./LimitDialog";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import CircleIcon from "@mui/icons-material/Circle";
import LessonLimitDialog from "./LessonLimitDialog";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { useSnackbar } from "./SnackbarHandler";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import QuizStartedNotification from "./QuizStartedNotification";
import FolderIcon from "@mui/icons-material/FolderOpen";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { TableChart as TableChartIcon } from "@mui/icons-material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AddIcon from "@mui/icons-material/Add";
import HelpDialog from "./HelpDialog";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CreateClassDialog from "./CreateClassDialog";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { TextField, InputAdornment } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import getListStyles from "./listStyles";
import ConstructionIcon from "@mui/icons-material/Construction";
import HomeIcon from "@mui/icons-material/Home";
import ChatIcon from "@mui/icons-material/Chat";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";

import ImageBank from "./ImageBank";
import FlashcardsBank from "./FlashcardsBank";
import QuizBank from "./QuizBank";
import LessonBankDialog from "./LessonBankDialog";
import TeacherCategoriesSection from "./TeacherCategoriesSection";
import Logger from "./Logger";

import AttachmentIcon from "@mui/icons-material/Attachment";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import GridViewIcon from "@mui/icons-material/GridView";
import ClearIcon from "@mui/icons-material/Clear";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SettingsPopover from "./SettingsPopover";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import Person3Icon from "@mui/icons-material/Person3";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NoteIcon from "@mui/icons-material/Note";
import MenuOpenIcon from "@mui/icons-material/MenuBook";
import { Grading as GradingIcon } from "@mui/icons-material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings"; // Add this import
import LightModeIcon from "@mui/icons-material/LightMode";
import { ClipLoader } from "react-spinners";
import LoadingSpinner from "./LoadingSpinner";
import CastForEducationIcon from "@mui/icons-material/CastForEducation";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from "@mui/icons-material/Language";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import QuizIcon from "@mui/icons-material/Quiz";
import LogoutIcon from "@mui/icons-material/Logout";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { SvgIcon } from "@mui/material";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import { useTheme } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import { useMediaQuery } from "@mui/material";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

import FeedbackIcon from "@mui/icons-material/Feedback";
import FeedbackDialog from "./FeedbackDialog";
import MicIcon from "@mui/icons-material/Mic";
import TeacherList from "./TeacherList";
import { shortcuts } from "./DashboardToolsShortcuts";
import { db } from "./firebase";
import { useAuth } from "./useAuth";
import {
    collection,
    addDoc,
    deleteDoc,
    getDocs,
    getDoc,
    query,
    where,
    doc,
    onSnapshot,
} from "firebase/firestore";
import { updateUserTokens } from "./functions";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import CreateIcon from "@mui/icons-material/Create";
import DescriptionIcon from "@mui/icons-material/Description";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { getStyles } from "../index";
import { signOut } from "firebase/auth";
import { auth } from "./firebase";

const Sidebar = ({
    handleDialogOpen,
    isOpen,
    toggleSidebar,
    isDark,
    toggleDarkMode,
    setOverrideTheme,
    minimized,
    setMinimized,
    miniDrawerWidth,
    toggleMinimizedSidebar,
}) => {
    const { user, updateUser, justLoggedIn, setJustLoggedIn } = useAuth();
    const theme = useTheme();

    const styles = getStyles(theme);
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [openOverviewDialog, setOpenOverviewDialog] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const navigate = useNavigate();
    const [openDialog, setOpenDialog] = useState(false);
    const [isLogin, setIsLogin] = useState(true);

    const [lessonCount, setLessonCount] = useState(0);
    const [lessonLimit, setLessonLimit] = useState(
        process.env.REACT_APP_LESSON_GENERATION_LIMIT,
    );
    const [lessonLimitExceeded, setLessonLimitExceeded] = useState(false);

    const [activeItem, setActiveItem] = useState("Hem");
    const [anchorEl, setAnchorEl] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isCreateClassDialogOpen, setIsCreateClassDialogOpen] =
        useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const [lessonLimitDialogOpen, setLessonLimitDialogOpen] = useState(false);

    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);

    const [showGuestItem, setShowGuestItem] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [tokens, setTokens] = useState(0);

    const [chatsExpanded, setChatsExpanded] = useState(false);

    const [quizMenuExpanded, setQuizMenuExpanded] = useState(false);
    const [flashcardsMenuExpanded, setFlashcardsMenuExpanded] = useState(false);
    const [podcastMenuExpanded, setPodcastMenuExpanded] = useState(false);
    const [imagesMenuExpanded, setImagesMenuExpanded] = useState(false);
    const { showSnackbar } = useSnackbar();
    const [lessonsMenuExpanded, setLessonsMenuExpanded] = useState(false);

    const [lessonPlanMenuExpanded, setLessonPlanMenuExpanded] = useState(false);
    const [hjalpmedelMenuExpanded, setHjalpmedelMenuExpanded] = useState(false);

    const [gradingMenuExpanded, setGradingzMenuExpanded] = useState(false);

    const [openImageBank, setOpenImageBank] = useState(false);
    const [openFlashcardsBank, setOpenFlashcardsBank] = useState(false);
    const [openLessonBank, setOpenLessonBank] = useState(false);

    const [openQuizBank, setOpenQuizBank] = useState(false);

    const userRef = useRef(user);
    const [expandedCategories, setExpandedCategories] = useState({});

    const categoryIcons = {
        Hjälpmedel: ConstructionIcon,
        Lektioner: SchoolIcon,
        Bedömning: AssignmentTurnedInIcon,
        Chatta: ChatIcon,
        Planering: CalendarMonthIcon,
    };

    // const teacherShortcuts = shortcuts.filter(
    //     (shortcut) =>
    //         shortcut.roles.includes("teacher") &&
    //         !["Lektioner", "Läsförståelse"].includes(shortcut.category),
    // );

    const [userDisplayName, setUserDisplayName] = useState(user?.displayName);
    // useEffect(() => {
    //     setUserDisplayName(auth.currentUser?.displayName);
    // }, [auth.currentUser?.displayName]);

    useEffect(() => {
        Logger.log("uname: ", auth.currentUser?.displayName);
        Logger.log("uname dis: ", user?.displayName);
        if (auth.currentUser?.displayName) {
            setUserDisplayName(auth.currentUser.displayName);
        }
    }, [auth.currentUser?.displayName]);

    const [hiddenMenus, setHiddenMenus] = useState([]);

    // Add this useEffect using the existing user data
    useEffect(() => {
        if (!user?.uid || !user?.rooms) return;

        async function fetchRoomShortcuts() {
            // If user has rooms, get shortcuts from the first one
            if (user.rooms && user.rooms.length > 0) {
                try {
                    const roomDoc = await getDoc(
                        doc(db, "rooms", user.rooms[0]),
                    );
                    if (roomDoc.exists()) {
                        setHiddenMenus(roomDoc.data()?.shortcuts || []);
                    }
                } catch (error) {
                    console.error("Error fetching room:", error);
                }
            }
        }

        fetchRoomShortcuts();
    }, [user]);

    useEffect(() => {
        if (user?.displayName) {
            setUserDisplayName(user.displayName);
        }
    }, [user?.displayName]);
    const teacherShortcuts = shortcuts.filter((shortcut) =>
        shortcut.roles.includes("teacher"),
    );

    const filteredTeacherShortcuts = teacherShortcuts.filter(
        (shortcut) =>
            shortcut.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (shortcut.description &&
                shortcut.description
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())),
    );

    const handleClassCreated = async (newClass) => {
        Logger.log("newClass created: ", newClass);
        navigate(`/app/klasser/${newClass.id}`);

        setIsCreateClassDialogOpen(false);
    };

    const maxTokens = user.guest
        ? process.env.REACT_APP_GUEST_TOKEN_LIMIT
        : process.env.REACT_APP_REGISTERED_TOKEN_LIMIT;
    const tokensExceeded = tokens > maxTokens;

    const [accountType, setAccountType] = useState(
        user?.accountType || "teacher",
    );

    const excludeCategories = ["Chatbotar", ""];
    if (accountType === "student") {
        excludeCategories.push("Planering", "Lektioner");
    }

    // Filter and get unique categories
    let teacherCategories = [
        ...new Set(
            filteredTeacherShortcuts
                .filter(
                    (shortcut) =>
                        !excludeCategories.includes(shortcut.category),
                )
                .map((shortcut) => shortcut.category),
        ),
    ];

    // If not student, sort specific categories first
    if (accountType !== "student") {
        teacherCategories.sort((a, b) => {
            const order = ["Lektioner", "Planering", "Bedömning"];
            return (
                (order.indexOf(a) === -1 ? 999 : order.indexOf(a)) -
                (order.indexOf(b) === -1 ? 999 : order.indexOf(b))
            );
        });
    }
    // Keep all your original state variables
    const [untrainedFlashcardCount, setUntrainedFlashcardCount] = useState(0);
    const [untrainedQuizCount, setUntrainedQuizCount] = useState(0);
    const [undownloadedFiles, setUndownloadedFiles] = useState(0);
    const [unOpenedLessons, setUnOpenedLessons] = useState(0);
    const [teacherSharedQuizCount, setTeacherSharedQuizCount] = useState(0);
    const [teacherSharedFlashcardCount, setTeacherSharedFlashcardCount] =
        useState(0);
    const [teacherSharedFileCount, setTeacherSharedFileCount] = useState(0);
    const [teacherSharedLessonsCount, setTeacherSharedLessonsCount] =
        useState(0);

    useEffect(() => {
        if (!user?.uid) return;

        // Student content
        const unsubscribeFlashcards = onSnapshot(
            query(
                collection(db, "flashcardCollections"),
                where("shared", "==", true),
            ),
            (querySnapshot) => {
                const untrainedCount = querySnapshot.docs.filter((doc) => {
                    const data = doc.data();
                    return (
                        data.userId !== user.uid &&
                        data.sharedTo?.includes(user.uid) &&
                        !data.lastTrainingSession
                    );
                }).length;
                setUntrainedFlashcardCount(untrainedCount);
            },
        );

        const unsubscribeQuizzes = onSnapshot(
            query(collection(db, "quiz"), where("shared", "==", true)),
            (querySnapshot) => {
                const untrainedCount = querySnapshot.docs.filter((doc) => {
                    const data = doc.data();
                    return (
                        data.userId !== user.uid &&
                        data.sharedTo?.includes(user.uid) &&
                        !data.lastTrainingSession
                    );
                }).length;
                setUntrainedQuizCount(untrainedCount);
            },
        );

        const unsubscribeFiles = onSnapshot(
            query(collection(db, "files"), where("shared", "==", true)),
            (querySnapshot) => {
                const untrainedCount = querySnapshot.docs.filter((doc) => {
                    const data = doc.data();
                    return (
                        data.userId !== user.uid &&
                        data.sharedTo?.includes(user.uid) &&
                        !data.downloaded
                    );
                }).length;
                setUndownloadedFiles(untrainedCount);
            },
        );

        const unsubscribeSharedLessons = onSnapshot(
            query(collection(db, "lesson_usage"), where("shared", "==", true)),
            (querySnapshot) => {
                const count = querySnapshot.docs.filter((doc) => {
                    const data = doc.data();
                    return (
                        data.userId !== user.uid &&
                        data.sharedTo?.includes(user.uid) &&
                        !data.hasOpened
                    );
                }).length;
                setUnOpenedLessons(count);
            },
        );

        // Teacher content
        const unsubscribeTeacherQuizzes = onSnapshot(
            query(
                collection(db, "quiz"),
                where("shared", "==", true),
                where("userId", "==", user.uid),
            ),
            (querySnapshot) => {
                const count = querySnapshot.docs.filter((doc) => {
                    return !doc.data().lastTrainingSession;
                }).length;
                setTeacherSharedQuizCount(count);
            },
        );

        const unsubscribeTeacherFlashcards = onSnapshot(
            query(
                collection(db, "flashcardCollections"),
                where("shared", "==", true),
                where("userId", "==", user.uid),
            ),
            (querySnapshot) => {
                const count = querySnapshot.docs.filter((doc) => {
                    return !doc.data().lastTrainingSession;
                }).length;
                setTeacherSharedFlashcardCount(count);
            },
        );

        const unsubscribeTeacherFiles = onSnapshot(
            query(
                collection(db, "files"),
                where("shared", "==", true),
                where("userId", "==", user.uid),
            ),
            (querySnapshot) => {
                const count = querySnapshot.docs.filter((doc) => {
                    return !doc.data().downloaded;
                }).length;
                setTeacherSharedFileCount(count);
            },
        );

        const unsubscribeTeacherLessons = onSnapshot(
            query(
                collection(db, "lesson_usage"),
                where("shared", "==", true),
                where("userId", "==", user.uid),
            ),
            (querySnapshot) => {
                const count = querySnapshot.docs.filter((doc) => {
                    return !doc.data().hasOpened;
                }).length;
                setTeacherSharedLessonsCount(count);
            },
        );

        return () => {
            unsubscribeFlashcards();
            unsubscribeQuizzes();
            unsubscribeFiles();
            unsubscribeSharedLessons();
            unsubscribeTeacherQuizzes();
            unsubscribeTeacherFlashcards();
            unsubscribeTeacherFiles();
            unsubscribeTeacherLessons();
        };
    }, [user?.uid]);

    // 3. ListItems remain the same as before, just using these new count variables

    // Add this effect to listen for user document changes
    useEffect(() => {
        if (user?.uid) {
            const unsubscribe = onSnapshot(
                doc(db, "users", user.uid),
                (docSnapshot) => {
                    if (docSnapshot.exists()) {
                        const userData = docSnapshot.data();
                        setAccountType(userData.accountType || "student");
                    }
                },
            );

            return () => unsubscribe();
        }
    }, [user?.uid]);

    useEffect(() => {
        Logger.log("user updated in sidebar: ", user.displayName);
        Logger.log("acc type: ", user.accountType);
        userRef.current = user;
    }, [user]);

    useEffect(() => {
        if (justLoggedIn) {
            setJustLoggedIn(false);
            //navigate('/app');
        }
    }, [justLoggedIn, navigate, setJustLoggedIn]);

    useEffect(() => {
        if (user) {
            //Logger.log('User in Sidebar useEffect:', user);
            const unsubscribe = onSnapshot(
                doc(db, "users", user.uid),
                (doc) => {
                    if (doc.exists()) {
                        const newTokens = doc.data().tokens || 0;
                        Logger.log(
                            "New tokens:",
                            newTokens,
                            "Max tokens:",
                            maxTokens,
                        );
                        setTokens(newTokens);

                        if (
                            newTokens > maxTokens &&
                            window.location.pathname !==
                                "/app/skapa-konto?tokens=exceeded"
                        ) {
                            Logger.log(
                                "Redirecting to login due to exceeded tokens",
                            );
                            navigate("/app/skapa-konto?tokens=exceeded");
                        }
                    }
                },
            );
            return () => unsubscribe();
        } else {
            Logger.log("No user in Sidebar useEffect");
            setTokens(0);
        }
    }, [user, maxTokens, navigate]);

    useEffect(() => {
        if (user) {
            // Get start and end of current day in local timezone
            const now = new Date();
            const startOfDay = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
            );
            const endOfDay = new Date(startOfDay);
            endOfDay.setDate(endOfDay.getDate() + 1);

            const lessonQuery = query(
                collection(db, "lesson_usage"),
                where("userId", "==", user.uid),
                where("timestamp", ">=", startOfDay),
                where("timestamp", "<", endOfDay),
            );

            const unsubscribe = onSnapshot(lessonQuery, (snapshot) => {
                const count = snapshot.docs.length;
                setLessonCount(count);
                setLessonLimitExceeded(count > lessonLimit);

                // if (count > lessonLimit && window.location.pathname !== "/app/skapa-konto?lessons=exceeded") {
                //   Logger.log("Redirecting due to exceeded lesson limit");
                //   navigate("/app/skapa-konto?lessons=exceeded");
                // }
            });

            return () => unsubscribe();
        } else {
            setLessonCount(0);
            setLessonLimitExceeded(false);
        }
    }, [user, lessonLimit]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate("/app/logga-in");
        } catch (error) {
            console.error("Error logging out:", error);
            // Optionally, you can show an error message to the user here
        }
    };

    // useEffect(() => {

    //     // Moonshine. Tidigare: if (user && !user.guest) { // Tog bort user.guest så tokens uppdateras för dom ockås
    //     if (user) {
    //         const unsubscribe = onSnapshot(doc(db, "users", user.uid), (doc) => {
    //             if (doc.exists()) {
    //                 const newTokens = doc.data().tokens || 0;
    //                 setTokens(newTokens);
    //                 if (newTokens > maxTokens) {
    //                     navigate('/app/login');
    //                 }
    //             }
    //         });
    //         return () => unsubscribe();
    //     } else {
    //         setTokens(user?.tokens || 0);
    //         if (user?.tokens > maxTokens) {
    //             navigate('/app/login');
    //         }
    //     }
    // }, [user, maxTokens, navigate]);

    useEffect(() => {
        if (user.guest) {
            const timer = setTimeout(() => {
                setShowGuestItem(true);
            }, 4000); // Delay of 2 seconds

            return () => clearTimeout(timer);
        } else {
            setShowGuestItem(false);
        }
    }, [user.guest]);

    const handleDialogOpenInternal = (isLoginView) => {
        setIsLogin(isLoginView);
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const handleItemClick = (path, itemName) => {
        navigate(path);
        setActiveItem(itemName);
        setIsDrawerOpen(false);

        if (isMobile) {
            toggleSidebar();
        }
    };

    useEffect(() => {
        if (user) {
            setOpenDialog(false);
            Logger.log("user tokens: ", user.tokens);
        }
    }, [user]);

    const activeStyles = {
        color:
            theme.palette.themeType === "allakando"
                ? theme.palette.primary.hover
                : isDark
                  ? "#fff"
                  : "#8b5cf6",
        backgroundColor:
            theme.palette.themeType === "allakando"
                ? theme.palette.background.card
                : isDark
                  ? "#15202B"
                  : "#eee9ff",
        borderRadius: "7px",
    };

    const openFeedbackDialog = useCallback(() => {
        setFeedbackDialogOpen(true);
    }, []);

    const handleAvatarClick = (event) => {
        if (user.guest) {
            goToLogin();
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const openPopover = Boolean(anchorEl);

    const [teachers, setTeachers] = useState([]);

    const handleDeleteTeacher = async (teacherId) => {
        try {
            await deleteDoc(doc(db, "teachers", teacherId));
            setTeachers((prevTeachers) =>
                prevTeachers.filter((teacher) => teacher.id !== teacherId),
            );
        } catch (error) {
            console.error("Error deleting teacher:", error);
            // Hantera felet (t.ex. visa ett felmeddelande för användaren)
        }
    };

    useEffect(() => {
        if (user && !user.isTemporary) {
            const teachersCollection = collection(db, "teachers");
            const q = query(
                teachersCollection,
                where("userId", "==", user.uid),
            );

            Logger.log("getting teach from ", user.uid);

            const unsubscribeSnapshot = onSnapshot(q, (querySnapshot) => {
                const teachersList = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setTeachers(teachersList);
                setLoading(false);
            });
            return () => unsubscribeSnapshot();
        } else {
            setTeachers([]);
            setLoading(false);
        }
    }, [user]);

    const fetchTeachers = async (userId) => {
        try {
            const teachersCollection = collection(db, "teachers");
            const q = query(teachersCollection, where("userId", "==", userId));
            const teachersSnapshot = await getDocs(q);
            const teachersList = teachersSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setTeachers(teachersList);
        } catch (error) {
            console.error("Fel vid hämtning av lärare:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleTeacherClick = (teacherId) => {
        navigate(`/app/chat/${teacherId}`);
    };
    const onAddTeacher = () => {
        navigate("/app/larare/");
    };

    const goToLogin = () => {
        navigate("/app/skapa-konto/");
    };

    const onPublicTeachers = () => {
        navigate("/app/personal/");
    };

    useEffect(() => {
        //Logger.log("User object changed:", user);
        //Logger.log("New display name:", user.displayName);
    }, [user]);

    const { listItemStyles, listItemIconStyles, listItemTextStyles } =
        getListStyles(theme, theme.palette.mode === "dark", minimized);

    const chevronIcon = {
        fontSize: "20px",
        color: "text.secondary",
        "&.MuiSvgIcon-root": {
            color: "red",
        },
        marginLeft: "auto",
        display: "none",
    };

    return (
        <>
            <IconButton
                sx={{
                    display: { xs: "block", md: isOpen ? "none" : "block" },
                    zIndex: 999,
                    position: "absolute",
                    top: 13,
                    left: 8,
                }}
                onClick={toggleSidebar}
            >
                <MenuIcon sx={{ fontSize: "1.7rem" }} />
            </IconButton>

            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={isOpen}
                onClose={toggleSidebar}
                sx={{
                    backgroundColor: isDark ? "#192734" : "white",
                    width: minimized ? miniDrawerWidth : "270px",
                    flexShrink: 0,
                    transition: theme.transitions.create("width", {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    "& .MuiDrawer-paper": {
                        backgroundColor: isDark ? "#192734" : "white",
                        width: minimized ? miniDrawerWidth : "270px",
                        boxSizing: "border-box",
                        overflowX: "hidden",
                        transition: theme.transitions.create("width", {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                        display: {
                            xs: isOpen ? "block" : "none",
                            md: isOpen ? "block" : "none",
                        },
                    },
                }}
                PaperProps={{
                    sx: { overflowY: "hidden" },
                }}
            >
                {theme.palette.themeType === "allakando" ? (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 17,
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 1000,
                        }}
                    >
                        <img
                            src="/img/allakando-logo.svg"
                            alt="Allakando Logo"
                            style={{
                                height: "24px",
                                width: "auto",
                            }}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            textAlign: "center",
                            mt: 2.6,
                            backgroundColor: isDark ? "#192734" : "white",
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                                filter: isDark ? undefined : "brightness(1.4)",
                                boxShadow: isDark
                                    ? undefined
                                    : "0 0 15px rgba(255, 255, 255, 0.3)",
                            },
                        }}
                        onClick={() => {
                            if (minimized) {
                                setMinimized(false);
                            }
                            navigate("/app");
                            if (isMobile) toggleSidebar();
                        }}
                    >
                        <img
                            src={
                                minimized
                                    ? isDark
                                        ? "/img/AI.png"
                                        : "/img/AI.png"
                                    : isDark
                                      ? "/img/logo-dark-2.png"
                                      : "/img/logo-2.png"
                            }
                            alt="studera.ai"
                            style={{
                                height: "28px",
                                width: "auto",
                            }}
                        />
                    </Box>
                )}
                <HelpDialog
                    open={openOverviewDialog}
                    onClose={() => setOpenOverviewDialog(false)}
                    overview={true}
                    user={user}
                    accountType={accountType}
                />

                <div
                    style={{
                        position: "absolute",

                        bottom: user.isPremium ? 104 : 155,
                        //bottom: 104,
                        left: 0,

                        width: "100%",

                        height: "40px",

                        background: isDark
                            ? "linear-gradient(to bottom, rgba(25, 39, 52, 0), rgba(25, 39, 52, 0.85))"
                            : "linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.7))",

                        pointerEvents: "none",

                        zIndex: 99999999,
                    }}
                />
                {!minimized && (
                    <>
                        {!user.isPremium && (
                            <Button
                                variant="purp"
                                startIcon={<RocketLaunchIcon />}
                                sx={{
                                    position: "absolute",
                                    bottom: 115,
                                    borderBottom: 0,
                                    left: 18,
                                    width: "231px",
                                    fontWeight: 600,
                                    borderRadius: "8px",
                                    paddingTop: "9px",
                                    paddingBottom: "9px",
                                    fontSize: "0.87rem",
                                    height: "40px",
                                    overflow: "hidden",
                                    "& .MuiSvgIcon-root": {
                                        fontSize: "1.05rem",
                                    },
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: -50,
                                        left: -100,
                                        width: "70px",
                                        height: "200px",
                                        background: "rgba(255, 255, 255, 0.05)",
                                        transform: "rotate(10deg)",
                                        animation: "shimmerEffect 3s infinite",
                                        zIndex: 1,
                                        pointerEvents: "none",
                                    },
                                    "@keyframes shimmerEffect": {
                                        "0%": {
                                            transform:
                                                "translateX(-200px) rotate(10deg)",
                                        },
                                        "100%": {
                                            transform:
                                                "translateX(400px) rotate(10deg)",
                                        },
                                    },
                                    "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        inset: 0,
                                        zIndex: 0,
                                        overflow: "hidden",
                                    },
                                }}
                                onClick={() => setLessonLimitDialogOpen(true)}
                                //onClick={() => setIsDialogOpen(true)}
                            >
                                {/* Moving dots in background */}
                                <span
                                    style={{
                                        position: "absolute",
                                        width: "8px",
                                        height: "8px",
                                        borderRadius: "50%",
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.6)",
                                        top: "20%",
                                        left: "15%",
                                        animation:
                                            "moveDot1 10s infinite linear, opacityChange1 7s infinite alternate",
                                        zIndex: 0,
                                    }}
                                />
                                <span
                                    style={{
                                        position: "absolute",
                                        width: "5px",
                                        height: "5px",
                                        borderRadius: "50%",
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.5)",
                                        top: "60%",
                                        left: "40%",
                                        animation:
                                            "moveDot2 13s infinite linear, opacityChange2 8s infinite alternate",
                                        zIndex: 0,
                                    }}
                                />
                                <span
                                    style={{
                                        position: "absolute",
                                        width: "6px",
                                        height: "6px",
                                        borderRadius: "50%",
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.55)",
                                        top: "30%",
                                        left: "70%",
                                        animation:
                                            "moveDot3 15s infinite linear, opacityChange3 6s infinite alternate",
                                        zIndex: 0,
                                    }}
                                />
                                <span
                                    style={{
                                        position: "absolute",
                                        width: "4px",
                                        height: "4px",
                                        borderRadius: "50%",
                                        backgroundColor:
                                            "rgba(255, 255, 255, 0.45)",
                                        top: "75%",
                                        left: "85%",
                                        animation:
                                            "moveDot4 11s infinite linear, opacityChange4 9s infinite alternate",
                                        zIndex: 0,
                                    }}
                                />
                                <style jsx>{`
                                    @keyframes moveDot1 {
                                        0% {
                                            transform: translate(0, 0);
                                        }
                                        25% {
                                            transform: translate(10px, 15px);
                                        }
                                        50% {
                                            transform: translate(20px, 0);
                                        }
                                        75% {
                                            transform: translate(10px, -15px);
                                        }
                                        100% {
                                            transform: translate(0, 0);
                                        }
                                    }
                                    @keyframes moveDot2 {
                                        0% {
                                            transform: translate(0, 0);
                                        }
                                        33% {
                                            transform: translate(-15px, 10px);
                                        }
                                        66% {
                                            transform: translate(15px, 10px);
                                        }
                                        100% {
                                            transform: translate(0, 0);
                                        }
                                    }
                                    @keyframes moveDot3 {
                                        0% {
                                            transform: translate(0, 0);
                                        }
                                        25% {
                                            transform: translate(-10px, -10px);
                                        }
                                        50% {
                                            transform: translate(-15px, 5px);
                                        }
                                        75% {
                                            transform: translate(5px, 10px);
                                        }
                                        100% {
                                            transform: translate(0, 0);
                                        }
                                    }
                                    @keyframes moveDot4 {
                                        0% {
                                            transform: translate(0, 0);
                                        }
                                        25% {
                                            transform: translate(8px, -12px);
                                        }
                                        50% {
                                            transform: translate(-8px, -8px);
                                        }
                                        75% {
                                            transform: translate(-12px, 12px);
                                        }
                                        100% {
                                            transform: translate(0, 0);
                                        }
                                    }
                                    @keyframes opacityChange1 {
                                        0% {
                                            opacity: 0.2;
                                        }
                                        100% {
                                            opacity: 0.5;
                                        }
                                    }
                                    @keyframes opacityChange2 {
                                        0% {
                                            opacity: 0.3;
                                        }
                                        100% {
                                            opacity: 0.6;
                                        }
                                    }
                                    @keyframes opacityChange3 {
                                        0% {
                                            opacity: 0.15;
                                        }
                                        100% {
                                            opacity: 0.45;
                                        }
                                    }
                                    @keyframes opacityChange4 {
                                        0% {
                                            opacity: 0.25;
                                        }
                                        100% {
                                            opacity: 0.55;
                                        }
                                    }
                                `}</style>
                                <span
                                    style={{ position: "relative", zIndex: 2 }}
                                >
                                    Uppgradera konto
                                </span>
                            </Button>
                        )}
                    </>
                )}
                <Tooltip
                    title="Guide"
                    placement={minimized ? "right" : "bottom"}
                >
                    <IconButton
                        sx={{
                            zIndex: 999,
                            position: "absolute",
                            bottom: 67,
                            right: minimized ? 11 : 20,
                            background: isDark
                                ? "rgb(40 57 71)"
                                : "rgb(225 225 225 / 49%)",
                            color: isDark
                                ? "rgb(255 255 255 / 77%)"
                                : "rgb(0 0 0 / 86%)",
                            borderRadius: "8px",
                            width: minimized ? "40px" : "72px",
                            padding: "7px",
                            "&:hover .youtube-icon": {
                                transform: "scale(1.2) rotate(-7deg)",
                                transition: "transform 0.3s ease",
                            },
                        }}
                        onClick={() => setOpenOverviewDialog(true)}
                    >
                        <YouTubeIcon
                            className="youtube-icon"
                            sx={{
                                opacity: "0.5",
                                fontSize: "1.4rem",
                                transition: "transform 0.3s ease",
                            }}
                        />
                    </IconButton>
                </Tooltip>
                {!minimized && (
                    <>
                        <Tooltip title="Språk" placement="bottom">
                            <IconButton
                                sx={{
                                    zIndex: 999,
                                    position: "absolute",
                                    bottom: 67,
                                    right: 100,
                                    background: isDark
                                        ? "rgb(40 57 71)"
                                        : "rgb(225 225 225 / 49%)",
                                    color: isDark
                                        ? "rgb(255 255 255 / 77%)"
                                        : "rgb(0 0 0 / 86%)",
                                    borderRadius: "8px",
                                    width: "72px",
                                    padding: "7px",
                                    "&:hover .youtube-icon": {
                                        transform: "scale(1.2) rotate(-7deg)",
                                        transition: "transform 0.3s ease",
                                    },
                                }}
                                onClick={() =>
                                    showSnackbar(
                                        "Stöd för fler språk är på väg :)",
                                        "info",
                                    )
                                }
                            >
                                <LanguageIcon
                                    className="youtube-icon"
                                    sx={{
                                        opacity: "0.5",
                                        fontSize: "1.4rem",
                                        transition: "transform 0.3s ease",
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                <Tooltip
                    title={isDark ? "Ljust tema" : "Mörkt tema"}
                    placement={minimized ? "right" : "bottom"}
                >
                    <IconButton
                        sx={{
                            zIndex: 999,
                            position: "absolute",
                            bottom: minimized ? 120 : 67,

                            right: minimized ? 11 : 180,
                            background: isDark
                                ? "rgb(40 57 71)"
                                : "rgb(225 225 225 / 49%)",
                            color: isDark
                                ? "rgb(255 255 255 / 77%)"
                                : "rgb(0 0 0 / 86%)",
                            borderRadius: "8px",
                            width: minimized ? "40px" : "72px",
                            padding: "7px",
                            "&:hover .youtube-icon": {
                                transform: "scale(1.2) rotate(-7deg)",
                                transition: "transform 0.3s ease",
                            },
                        }}
                        onClick={toggleDarkMode}
                    >
                        {isDark ? (
                            <DarkModeIcon
                                className="youtube-icon"
                                sx={{
                                    opacity: "0.5",
                                    fontSize: "1.4rem",
                                }}
                            />
                        ) : (
                            <LightModeIcon
                                className="youtube-icon"
                                sx={{
                                    opacity: "0.5",
                                    fontSize: "1.4rem",
                                }}
                            />
                        )}
                    </IconButton>
                </Tooltip>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        background: isDark ? "#192734" : "white",
                    }}
                >
                    <Box
                        sx={{
                            flexGrow: 1,
                            overflowY: "auto",
                            mt: theme.palette.themeType === "allakando" ? 8 : 1,
                            marginBottom: user.isPremium ? "160px" : "211px",
                            //marginBottom: "160px",
                            //marginBottom: "65px",
                            paddingBottom: "20px",

                            "&::-webkit-scrollbar": {
                                width: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                                background: "transparent",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                background: isDark
                                    ? "rgba(255, 255, 255, 0.05)" // Lighter default for dark mode
                                    : "rgb(0 0 0 / 34%) transparent", // Lighter default for light mode
                                borderRadius: "4px",
                                "&:hover": {
                                    background: isDark
                                        ? "rgba(255, 255, 255, 0.1)" // Previous default color now on hover
                                        : "rgba(0, 0, 0, 0.06)", // Previous default color now on hover
                                },
                            },
                            // For Firefox
                            scrollbarWidth: "auto",
                            scrollbarColor: isDark
                                ? "rgba(255, 255, 255, 0.05) transparent" // Matching the webkit lighter color
                                : "rgb(0 0 0 / 14%) transparent", // Matching the webkit lighter color
                        }}
                    >
                        {loading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100%",
                                }}
                            >
                                <LoadingSpinner loading={loading} />
                                {/*<LoadingSpinner size="20" noWrapper="true" /> */}
                            </Box>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        display:
                                            accountType === "teacher" ||
                                            accountType === "substitute"
                                                ? "block"
                                                : "block",
                                    }}
                                >
                                    <Box sx={{ px: 2, py: 1, display: "none" }}>
                                        <TextField
                                            size="small"
                                            placeholder="Sök..."
                                            value={searchTerm}
                                            onChange={(e) =>
                                                setSearchTerm(e.target.value)
                                            }
                                            fullWidth
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "9999px",

                                                    backgroundColor: isDark
                                                        ? "#22303C"
                                                        : "#f4f4f5",
                                                    "& fieldset": {
                                                        borderColor:
                                                            "transparent",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor:
                                                            "transparent",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "#8b5cf6",
                                                    },
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon
                                                            sx={{
                                                                color: "#94a3b8",
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: searchTerm && (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                setSearchTerm(
                                                                    "",
                                                                )
                                                            }
                                                            sx={{
                                                                color: "#94a3b8",
                                                            }}
                                                        >
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>

                                    <List sx={{ py: 0, margin: "8px" }}>
                                        {/* Your new list item */}

                                        <ListItem
                                            button
                                            onClick={() => {
                                                setActiveItem("Hem");
                                                if (minimized) {
                                                    setMinimized(false);
                                                }
                                                navigate("/app");
                                                if (isMobile) toggleSidebar();
                                            }}
                                            sx={{
                                                ...listItemStyles,
                                                justifyContent: minimized
                                                    ? "center"
                                                    : "flex-start",
                                                paddingLeft: minimized ? 2 : 2,
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={listItemIconStyles}
                                            >
                                                <HomeIcon />
                                            </ListItemIcon>
                                            {!minimized && (
                                                <ListItemText
                                                    primary={"Hem"}
                                                    sx={listItemTextStyles}
                                                />
                                            )}
                                            {!minimized && (
                                                <ChevronRightIcon
                                                    className="chevron-icon"
                                                    sx={chevronIcon}
                                                />
                                            )}
                                        </ListItem>

                                        {/* For Chat */}
                                        {!hiddenMenus.includes("chat") && (
                                            <Tooltip
                                                title="Chat"
                                                placement="right"
                                                disableHoverListener={
                                                    !minimized
                                                }
                                                disableFocusListener={
                                                    !minimized
                                                }
                                                disableTouchListener={
                                                    !minimized
                                                }
                                            >
                                                <ListItem
                                                    button
                                                    component={Link}
                                                    to="/app/fraga-ai"
                                                    onClick={() => {
                                                        if (isMobile)
                                                            toggleSidebar();
                                                    }}
                                                    sx={{
                                                        ...listItemStyles,
                                                        justifyContent:
                                                            minimized
                                                                ? "center"
                                                                : "flex-start",
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={listItemIconStyles}
                                                    >
                                                        <ChatIcon />
                                                    </ListItemIcon>
                                                    {!minimized && (
                                                        <ListItemText
                                                            primary={"Chat"}
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    )}
                                                    {!minimized && (
                                                        <ChevronRightIcon
                                                            className="chevron-icon"
                                                            sx={chevronIcon}
                                                        />
                                                    )}
                                                </ListItem>
                                            </Tooltip>
                                        )}
                                        {user?.admin && (
                                            <>
                                                <ListItem
                                                    button
                                                    component={Link}
                                                    to="/app/lektionsbanken"
                                                    onClick={() => {
                                                        if (isMobile)
                                                            toggleSidebar();
                                                    }}
                                                    sx={{
                                                        ...listItemStyles,
                                                        display: "none",
                                                        ...(activeItem ===
                                                        "Admin"
                                                            ? activeStyles
                                                            : {}),
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={listItemIconStyles}
                                                    >
                                                        <CastForEducationIcon />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            "Lektionsbanken"
                                                        }
                                                        sx={listItemTextStyles}
                                                    />
                                                    <ChevronRightIcon
                                                        className="chevron-icon"
                                                        sx={chevronIcon}
                                                    />
                                                </ListItem>
                                                {!minimized && (
                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/hood"
                                                        onClick={() => {
                                                            setActiveItem(
                                                                "Admin",
                                                            );
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                            ...(activeItem ===
                                                            "Admin"
                                                                ? activeStyles
                                                                : {}),
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <AdminPanelSettingsIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={"Admin"}
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                        <ChevronRightIcon
                                                            className="chevron-icon"
                                                            sx={chevronIcon}
                                                        />
                                                    </ListItem>
                                                )}
                                            </>
                                        )}

                                        <List
                                            sx={{
                                                py: 0,
                                                margin: "0px",
                                                display:
                                                    accountType === "teacher" ||
                                                    accountType === "substitute"
                                                        ? "none"
                                                        : "block",
                                            }}
                                        >
                                            {accountType === "student" && (
                                                <>
                                                    {!minimized && (
                                                        <ListItem
                                                            button
                                                            onClick={() =>
                                                                setMenuOpen(
                                                                    !menuOpen,
                                                                )
                                                            }
                                                            sx={listItemStyles}
                                                        >
                                                            <ListItemIcon
                                                                sx={
                                                                    listItemIconStyles
                                                                }
                                                            >
                                                                <FolderIcon />
                                                            </ListItemIcon>
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    flex: 1,
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        "Mitt innehåll"
                                                                    }
                                                                    sx={
                                                                        listItemTextStyles
                                                                    }
                                                                />
                                                            </Box>
                                                            {menuOpen ? (
                                                                <ExpandLess
                                                                    sx={{
                                                                        marginLeft:
                                                                            "auto",
                                                                        color: "text.secondary",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <>
                                                                    {unOpenedLessons +
                                                                        untrainedQuizCount +
                                                                        untrainedFlashcardCount +
                                                                        undownloadedFiles >
                                                                        0 && (
                                                                        <Chip
                                                                            label={
                                                                                unOpenedLessons +
                                                                                untrainedQuizCount +
                                                                                untrainedFlashcardCount +
                                                                                undownloadedFiles
                                                                            }
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor:
                                                                                    isDark
                                                                                        ? "#2a3b4c"
                                                                                        : "#e0e7ff",
                                                                                color: isDark
                                                                                    ? "#60a5fa"
                                                                                    : "#3b82f6",
                                                                                fontWeight:
                                                                                    "bold",
                                                                                fontSize:
                                                                                    "0.7rem",
                                                                                height: "20px",
                                                                                marginLeft:
                                                                                    "8px",
                                                                            }}
                                                                        />
                                                                    )}
                                                                </>
                                                            )}
                                                        </ListItem>
                                                    )}
                                                    <Collapse
                                                        in={
                                                            menuOpen &&
                                                            !minimized
                                                        }
                                                        timeout="auto"
                                                        sx={{ ml: 1 }}
                                                    >
                                                        <ListItem
                                                            button
                                                            component={Link}
                                                            to="/app/mina-podcasts"
                                                            onClick={() => {
                                                                if (isMobile)
                                                                    toggleSidebar();
                                                            }}
                                                            sx={{
                                                                ...listItemStyles,
                                                            }}
                                                        >
                                                            <ListItemIcon
                                                                sx={
                                                                    listItemIconStyles
                                                                }
                                                            >
                                                                <MicIcon />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    "Podcasts"
                                                                }
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                        </ListItem>

                                                        <ListItem
                                                            button
                                                            component={Link}
                                                            to="/app/delade-lektioner"
                                                            onClick={() => {
                                                                if (isMobile)
                                                                    toggleSidebar();
                                                            }}
                                                            sx={listItemStyles}
                                                        >
                                                            <ListItemIcon
                                                                sx={
                                                                    listItemIconStyles
                                                                }
                                                            >
                                                                <BookmarkBorderIcon />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    "Lektioner"
                                                                }
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                            {unOpenedLessons >
                                                                0 && (
                                                                <Chip
                                                                    label={
                                                                        unOpenedLessons
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            isDark
                                                                                ? "#2a3b4c"
                                                                                : "#e0e7ff",
                                                                        color: isDark
                                                                            ? "#60a5fa"
                                                                            : "#3b82f6",
                                                                        fontWeight:
                                                                            "bold",
                                                                        fontSize:
                                                                            "0.7rem",
                                                                        height: "20px",
                                                                        marginLeft:
                                                                            "8px",
                                                                    }}
                                                                />
                                                            )}
                                                        </ListItem>

                                                        <ListItem
                                                            button
                                                            component={Link}
                                                            to="/app/mina-quiz"
                                                            onClick={() => {
                                                                if (isMobile)
                                                                    toggleSidebar();
                                                            }}
                                                            sx={listItemStyles}
                                                        >
                                                            <ListItemIcon
                                                                sx={
                                                                    listItemIconStyles
                                                                }
                                                            >
                                                                <PsychologyIcon />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={"Quiz"}
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                            {untrainedQuizCount >
                                                                0 && (
                                                                <Chip
                                                                    label={
                                                                        untrainedQuizCount
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            isDark
                                                                                ? "#2a3b4c"
                                                                                : "#e0e7ff",
                                                                        color: isDark
                                                                            ? "#60a5fa"
                                                                            : "#3b82f6",
                                                                        fontWeight:
                                                                            "bold",
                                                                        fontSize:
                                                                            "0.7rem",
                                                                        height: "20px",
                                                                        marginLeft:
                                                                            "8px",
                                                                    }}
                                                                />
                                                            )}
                                                        </ListItem>

                                                        <ListItem
                                                            button
                                                            component={Link}
                                                            to="/app/mina-flashcards"
                                                            onClick={() => {
                                                                if (isMobile)
                                                                    toggleSidebar();
                                                            }}
                                                            sx={listItemStyles}
                                                        >
                                                            <ListItemIcon
                                                                sx={
                                                                    listItemIconStyles
                                                                }
                                                            >
                                                                <ViewAgendaIcon />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    "Flashcards"
                                                                }
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                            {untrainedFlashcardCount >
                                                                0 && (
                                                                <Chip
                                                                    label={
                                                                        untrainedFlashcardCount
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            isDark
                                                                                ? "#2a3b4c"
                                                                                : "#e0e7ff",
                                                                        color: isDark
                                                                            ? "#60a5fa"
                                                                            : "#3b82f6",
                                                                        fontWeight:
                                                                            "bold",
                                                                        fontSize:
                                                                            "0.7rem",
                                                                        height: "20px",
                                                                        marginLeft:
                                                                            "8px",
                                                                    }}
                                                                />
                                                            )}
                                                        </ListItem>

                                                        <ListItem
                                                            button
                                                            component={Link}
                                                            to="/app/mina-filer"
                                                            onClick={() => {
                                                                if (isMobile)
                                                                    toggleSidebar();
                                                            }}
                                                            sx={listItemStyles}
                                                        >
                                                            <ListItemIcon
                                                                sx={
                                                                    listItemIconStyles
                                                                }
                                                            >
                                                                <AttachmentIcon />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    "Filer"
                                                                }
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                            {undownloadedFiles >
                                                                0 && (
                                                                <Chip
                                                                    label={
                                                                        undownloadedFiles
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            isDark
                                                                                ? "#2a3b4c"
                                                                                : "#e0e7ff",
                                                                        color: isDark
                                                                            ? "#60a5fa"
                                                                            : "#3b82f6",
                                                                        fontWeight:
                                                                            "bold",
                                                                        fontSize:
                                                                            "0.7rem",
                                                                        height: "20px",
                                                                        marginLeft:
                                                                            "8px",
                                                                    }}
                                                                />
                                                            )}
                                                        </ListItem>
                                                    </Collapse>
                                                </>
                                            )}
                                            <Divider sx={{ mt: 1, mb: 1 }} />
                                        </List>

                                        {accountType === "teacher" && (
                                            <>
                                                {!minimized && (
                                                    <ListItem
                                                        button
                                                        onClick={() =>
                                                            setMenuOpen(
                                                                !menuOpen,
                                                            )
                                                        }
                                                        sx={listItemStyles}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <FolderIcon />
                                                        </ListItemIcon>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                flex: 1,
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary="Mitt innehåll"
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                        </Box>
                                                        {menuOpen ? (
                                                            <ExpandLess
                                                                sx={{
                                                                    marginLeft:
                                                                        "auto",
                                                                    color: "text.secondary",
                                                                }}
                                                            />
                                                        ) : (
                                                            <>
                                                                {teacherSharedLessonsCount +
                                                                    teacherSharedQuizCount +
                                                                    teacherSharedFlashcardCount +
                                                                    teacherSharedFileCount >
                                                                    0 && (
                                                                    <Chip
                                                                        label={
                                                                            teacherSharedLessonsCount +
                                                                            teacherSharedQuizCount +
                                                                            teacherSharedFlashcardCount +
                                                                            teacherSharedFileCount
                                                                        }
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor:
                                                                                isDark
                                                                                    ? "#2a3b4c"
                                                                                    : "#e0e7ff",
                                                                            color: isDark
                                                                                ? "#60a5fa"
                                                                                : "#3b82f6",
                                                                            fontWeight:
                                                                                "bold",
                                                                            fontSize:
                                                                                "0.7rem",
                                                                            height: "20px",
                                                                            marginLeft:
                                                                                "8px",
                                                                        }}
                                                                    />
                                                                )}
                                                            </>
                                                        )}
                                                    </ListItem>
                                                )}
                                                <Collapse
                                                    in={menuOpen && !minimized}
                                                    timeout="auto"
                                                    sx={{ ml: 1 }}
                                                >
                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/rum"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <Groups2Icon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={"Elevrum"}
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </ListItem>

                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/uppsatser"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <AssignmentTurnedInIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                "Bedömningar"
                                                            }
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </ListItem>

                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/delade-lektioner"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                            ...(activeItem ===
                                                            "Admin"
                                                                ? activeStyles
                                                                : {}),
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <BookmarkBorderIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary="Lektioner"
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />

                                                        {teacherSharedLessonsCount >
                                                            0 && (
                                                            <Tooltip
                                                                title={`${teacherSharedLessonsCount} elever som inte öppnat än`}
                                                                placement="right"
                                                            >
                                                                <Chip
                                                                    label={
                                                                        teacherSharedLessonsCount
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            isDark
                                                                                ? "#2a3b4c"
                                                                                : "#e0e7ff",
                                                                        color: isDark
                                                                            ? "#60a5fa"
                                                                            : "#3b82f6",
                                                                        fontWeight:
                                                                            "bold",
                                                                        fontSize:
                                                                            "0.7rem",
                                                                        height: "20px",
                                                                        marginLeft:
                                                                            "8px",
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}

                                                        <ChevronRightIcon
                                                            className="chevron-icon"
                                                            sx={chevronIcon}
                                                        />
                                                    </ListItem>
                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/mina-podcasts"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <MicIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={"Podcasts"}
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </ListItem>

                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/mina-bilder"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <ImageIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={"Bilder"}
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </ListItem>

                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/mina-quiz"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                            ...(activeItem ===
                                                            "Admin"
                                                                ? activeStyles
                                                                : {}),
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <PsychologyIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary="Quiz"
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />

                                                        {teacherSharedQuizCount >
                                                            0 && (
                                                            <Tooltip
                                                                title={`${teacherSharedQuizCount} elever som inte börjat öva än`}
                                                                placement="right"
                                                            >
                                                                <Chip
                                                                    label={
                                                                        teacherSharedQuizCount
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            isDark
                                                                                ? "#2a3b4c"
                                                                                : "#e0e7ff",
                                                                        color: isDark
                                                                            ? "#60a5fa"
                                                                            : "#3b82f6",
                                                                        fontWeight:
                                                                            "bold",
                                                                        fontSize:
                                                                            "0.7rem",
                                                                        height: "20px",
                                                                        marginLeft:
                                                                            "8px",
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}

                                                        <ChevronRightIcon
                                                            className="chevron-icon"
                                                            sx={chevronIcon}
                                                        />
                                                    </ListItem>

                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/mina-flashcards"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                            ...(activeItem ===
                                                            "Admin"
                                                                ? activeStyles
                                                                : {}),
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <ViewAgendaIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary="Flashcards"
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />

                                                        {teacherSharedFlashcardCount >
                                                            0 && (
                                                            <Tooltip
                                                                title={`${teacherSharedFlashcardCount} elever som inte börjat öva än`}
                                                                placement="right"
                                                            >
                                                                <Chip
                                                                    label={
                                                                        teacherSharedFlashcardCount
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            isDark
                                                                                ? "#2a3b4c"
                                                                                : "#e0e7ff",
                                                                        color: isDark
                                                                            ? "#60a5fa"
                                                                            : "#3b82f6",
                                                                        fontWeight:
                                                                            "bold",
                                                                        fontSize:
                                                                            "0.7rem",
                                                                        height: "20px",
                                                                        marginLeft:
                                                                            "8px",
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}

                                                        <ChevronRightIcon
                                                            className="chevron-icon"
                                                            sx={chevronIcon}
                                                        />
                                                    </ListItem>

                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/mina-filer"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                            ...(activeItem ===
                                                            "Admin"
                                                                ? activeStyles
                                                                : {}),
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <AttachmentIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary="Filer"
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />

                                                        {teacherSharedFileCount >
                                                            0 && (
                                                            <Tooltip
                                                                title={`${teacherSharedFileCount} elever som inte laddat ned än`}
                                                                placement="right"
                                                            >
                                                                <Chip
                                                                    label={
                                                                        teacherSharedFileCount
                                                                    }
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            isDark
                                                                                ? "#2a3b4c"
                                                                                : "#e0e7ff",
                                                                        color: isDark
                                                                            ? "#60a5fa"
                                                                            : "#3b82f6",
                                                                        fontWeight:
                                                                            "bold",
                                                                        fontSize:
                                                                            "0.7rem",
                                                                        height: "20px",
                                                                        marginLeft:
                                                                            "8px",
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}

                                                        <ChevronRightIcon
                                                            className="chevron-icon"
                                                            sx={chevronIcon}
                                                        />
                                                    </ListItem>
                                                </Collapse>
                                                <Divider
                                                    sx={{ mt: 1, mb: 1.5 }}
                                                />
                                            </>
                                        )}
                                    </List>

                                    <List sx={{ py: 0, margin: "8px" }}>
                                        {!hiddenMenus.includes("bedomning") && (
                                            <Tooltip
                                                title="Bedömning"
                                                placement="right"
                                                disableHoverListener={
                                                    !minimized
                                                }
                                                disableFocusListener={
                                                    !minimized
                                                }
                                                disableTouchListener={
                                                    !minimized
                                                }
                                            >
                                                <ListItem
                                                    button
                                                    onClick={() => {
                                                        if (minimized) {
                                                            navigate(
                                                                "/app/uppsats/ny",
                                                            );
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        } else {
                                                            setGradingzMenuExpanded(
                                                                !gradingMenuExpanded,
                                                            );
                                                        }
                                                    }}
                                                    sx={{
                                                        ...listItemStyles,
                                                        marginBottom: "3px",
                                                        justifyContent:
                                                            minimized
                                                                ? "center"
                                                                : "flex-start",
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={listItemIconStyles}
                                                    >
                                                        <AssignmentTurnedInIcon />
                                                    </ListItemIcon>
                                                    {!minimized && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                flex: 1,
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    "Bedömning"
                                                                }
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                        </Box>
                                                    )}
                                                    {!minimized &&
                                                        (gradingMenuExpanded ? (
                                                            <ExpandLess
                                                                sx={{
                                                                    marginLeft:
                                                                        "auto",
                                                                    color: "text.secondary",
                                                                }}
                                                            />
                                                        ) : (
                                                            <ExpandMore
                                                                sx={{
                                                                    marginLeft:
                                                                        "auto",
                                                                    color: isDark
                                                                        ? "text.secondary"
                                                                        : "#94a3b8",
                                                                }}
                                                            />
                                                        ))}
                                                </ListItem>
                                            </Tooltip>
                                        )}
                                        <Collapse
                                            in={
                                                gradingMenuExpanded &&
                                                !minimized
                                            }
                                            timeout="auto"
                                            sx={{
                                                ml: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    pl: 2,
                                                    py: 1,
                                                    color: "text.secondary",
                                                    fontSize: "12px",
                                                    fontWeight: 500,

                                                    background:
                                                        "linear-gradient(to right, rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0))",
                                                    mb: 1,
                                                    mt: 1.5,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.08em",
                                                    display: "none",
                                                }}
                                            >
                                                Bedömning
                                            </Box>
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/uppsats/ny"
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <AddCircleIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Ny bedömning"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>

                                            {accountType === "teacher" && (
                                                <>
                                                    <ListItem
                                                        button
                                                        onClick={() =>
                                                            setIsCreateClassDialogOpen(
                                                                true,
                                                            )
                                                        }
                                                        sx={{
                                                            ...listItemStyles,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <AddCircleIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={"Ny klass"}
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                        <ChevronRightIcon
                                                            className="chevron-icon"
                                                            sx={chevronIcon}
                                                        />
                                                    </ListItem>
                                                </>
                                            )}
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/matris/ny"
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <AddCircleIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Ny matris"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/uppsatser"
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <ViewAgendaIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Mina bedömningar"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>

                                            {accountType === "teacher" && (
                                                <>
                                                    <Box
                                                        sx={{
                                                            pl: 2,
                                                            py: 1,
                                                            color: "text.secondary",
                                                            fontSize: "12px",
                                                            fontWeight: 500,

                                                            background:
                                                                "linear-gradient(to right, rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0))",
                                                            mb: 1,
                                                            mt: 1.5,
                                                            textTransform:
                                                                "uppercase",
                                                            letterSpacing:
                                                                "0.08em",
                                                            display: "none",
                                                        }}
                                                    >
                                                        Klasser
                                                    </Box>

                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/klasser"
                                                        sx={{
                                                            ...listItemStyles,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <SchoolIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                "Mina klasser"
                                                            }
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </ListItem>
                                                </>
                                            )}
                                            <Box
                                                sx={{
                                                    pl: 2,
                                                    py: 1,
                                                    color: "text.secondary",
                                                    fontSize: "12px",
                                                    fontWeight: 500,

                                                    background:
                                                        "linear-gradient(to right, rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0))",
                                                    mb: 1,
                                                    mt: 1.5,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.08em",
                                                    display: "none",
                                                }}
                                            >
                                                Bedömningsmatriser
                                            </Box>

                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/matris/mina"
                                                onClick={() => {
                                                    setActiveItem("Hem");
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <TableChartIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Mina matriser"}
                                                    sx={listItemTextStyles}
                                                />
                                                <ChevronRightIcon
                                                    className="chevron-icon"
                                                    sx={chevronIcon}
                                                />
                                            </ListItem>
                                            <Divider sx={{ mt: 2, mb: 2 }} />
                                        </Collapse>

                                        {accountType === "teacher" && (
                                            <>
                                                <Tooltip
                                                    title="Lektioner"
                                                    placement="right"
                                                    disableHoverListener={
                                                        !minimized
                                                    }
                                                >
                                                    <ListItem
                                                        button
                                                        onClick={() => {
                                                            if (minimized) {
                                                                navigate(
                                                                    "/app/lektioner",
                                                                );
                                                                if (isMobile)
                                                                    toggleSidebar();
                                                            } else {
                                                                setLessonsMenuExpanded(
                                                                    !lessonsMenuExpanded,
                                                                );
                                                            }
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                            marginBottom: "3px",
                                                            justifyContent:
                                                                minimized
                                                                    ? "center"
                                                                    : "flex-start",
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <SchoolIcon />
                                                        </ListItemIcon>
                                                        {!minimized && (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    flex: 1,
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        "Lektioner"
                                                                    }
                                                                    sx={
                                                                        listItemTextStyles
                                                                    }
                                                                />
                                                            </Box>
                                                        )}
                                                        {!minimized &&
                                                            (lessonsMenuExpanded ? (
                                                                <ExpandLess
                                                                    sx={{
                                                                        marginLeft:
                                                                            "auto",
                                                                        color: isDark
                                                                            ? "text.secondary"
                                                                            : "#94a3b8",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <ExpandMore
                                                                    sx={{
                                                                        marginLeft:
                                                                            "auto",
                                                                        color: isDark
                                                                            ? "text.secondary"
                                                                            : "#94a3b8",
                                                                    }}
                                                                />
                                                            ))}
                                                    </ListItem>
                                                </Tooltip>

                                                <Collapse
                                                    in={
                                                        lessonsMenuExpanded &&
                                                        !minimized
                                                    }
                                                    timeout="auto"
                                                    sx={{ ml: 1 }}
                                                >
                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/lektioner"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <GenerateIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                "Generera lektion"
                                                            }
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </ListItem>
                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/delade-lektioner"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <FolderIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary="Lektioner"
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </ListItem>

                                                    <Divider
                                                        sx={{ mt: 2, mb: 2 }}
                                                    />
                                                </Collapse>
                                            </>
                                        )}
                                        {accountType === "teacher" && (
                                            <>
                                                <Tooltip
                                                    title="Planering"
                                                    placement="right"
                                                    disableHoverListener={
                                                        !minimized
                                                    }
                                                    disableFocusListener={
                                                        !minimized
                                                    }
                                                    disableTouchListener={
                                                        !minimized
                                                    }
                                                >
                                                    <ListItem
                                                        button
                                                        onClick={() => {
                                                            if (minimized) {
                                                                navigate(
                                                                    "/app/lektioner/lektionsplan",
                                                                );
                                                                if (isMobile)
                                                                    toggleSidebar();
                                                            } else {
                                                                setLessonPlanMenuExpanded(
                                                                    !lessonPlanMenuExpanded,
                                                                );
                                                            }
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                            justifyContent:
                                                                minimized
                                                                    ? "center"
                                                                    : "flex-start",
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <CalendarMonthIcon />
                                                        </ListItemIcon>
                                                        {!minimized && (
                                                            <ListItemText
                                                                primary={
                                                                    "Planering"
                                                                }
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                        )}
                                                        {!minimized &&
                                                            (lessonPlanMenuExpanded ? (
                                                                <ExpandLess
                                                                    sx={{
                                                                        marginLeft:
                                                                            "auto",
                                                                        color: isDark
                                                                            ? "text.secondary"
                                                                            : "#94a3b8",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <ExpandMore
                                                                    sx={{
                                                                        marginLeft:
                                                                            "auto",
                                                                        color: isDark
                                                                            ? "text.secondary"
                                                                            : "#94a3b8",
                                                                    }}
                                                                />
                                                            ))}
                                                    </ListItem>
                                                </Tooltip>

                                                <Collapse
                                                    in={
                                                        lessonPlanMenuExpanded &&
                                                        !minimized
                                                    }
                                                    timeout="auto"
                                                    sx={{ ml: 1 }}
                                                >
                                                    <ListItem
                                                        button
                                                        component={Link}
                                                        to="/app/lektioner/lektionsplan"
                                                        onClick={() => {
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        }}
                                                        sx={{
                                                            ...listItemStyles,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={
                                                                listItemIconStyles
                                                            }
                                                        >
                                                            <GenerateIcon />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                "Ny lektionsplanering"
                                                            }
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </ListItem>

                                                    <Divider
                                                        sx={{ mt: 2, mb: 2 }}
                                                    />
                                                </Collapse>
                                            </>
                                        )}
                                        {/* Modify the Quiz menu item */}

                                        <Tooltip
                                            title="Quiz"
                                            placement="right"
                                            disableHoverListener={!minimized}
                                            disableFocusListener={!minimized}
                                            disableTouchListener={!minimized}
                                        >
                                            <ListItem
                                                button
                                                onClick={() => {
                                                    if (minimized) {
                                                        navigate("/app/quiz");
                                                        if (isMobile)
                                                            toggleSidebar();
                                                    } else {
                                                        setQuizMenuExpanded(
                                                            !quizMenuExpanded,
                                                        );
                                                    }
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                    marginBottom: "3px",
                                                    justifyContent: minimized
                                                        ? "center"
                                                        : "flex-start",
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <PsychologyIcon />
                                                </ListItemIcon>
                                                {!minimized && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={"Quiz"}
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                                {!minimized &&
                                                    (quizMenuExpanded ? (
                                                        <ExpandLess
                                                            sx={{
                                                                marginLeft:
                                                                    "auto",
                                                                color: isDark
                                                                    ? "text.secondary"
                                                                    : "#94a3b8",
                                                            }}
                                                        />
                                                    ) : (
                                                        <ExpandMore
                                                            sx={{
                                                                marginLeft:
                                                                    "auto",
                                                                color: isDark
                                                                    ? "text.secondary"
                                                                    : "#94a3b8",
                                                            }}
                                                        />
                                                    ))}
                                            </ListItem>
                                        </Tooltip>

                                        <Collapse
                                            in={quizMenuExpanded && !minimized}
                                            timeout="auto"
                                            sx={{
                                                ml: 1,
                                            }}
                                        >
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/quiz"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <GenerateIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Generera quiz"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>
                                            <ListItem
                                                button
                                                onClick={() =>
                                                    setOpenQuizBank(true)
                                                }
                                                sx={{
                                                    ...listItemStyles,
                                                    display: "none",
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <CircleIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Quiz-banken"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/mina-quiz"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <FolderOpenIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Mina quiz"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>

                                            <Divider sx={{ mt: 2, mb: 2 }} />
                                        </Collapse>

                                        {/* Modify the Flashcards menu item */}
                                        <Tooltip
                                            title="Flashcards"
                                            placement="right"
                                            disableHoverListener={!minimized}
                                            disableFocusListener={!minimized}
                                            disableTouchListener={!minimized}
                                        >
                                            <ListItem
                                                button
                                                onClick={() => {
                                                    if (minimized) {
                                                        navigate(
                                                            "/app/flashcards",
                                                        );
                                                        if (isMobile)
                                                            toggleSidebar();
                                                    } else {
                                                        setFlashcardsMenuExpanded(
                                                            !flashcardsMenuExpanded,
                                                        );
                                                    }
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                    marginBottom: "3px",
                                                    justifyContent: minimized
                                                        ? "center"
                                                        : "flex-start",
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <ViewAgendaIcon />
                                                </ListItemIcon>
                                                {!minimized && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                "Flashcards"
                                                            }
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                                {!minimized &&
                                                    (flashcardsMenuExpanded ? (
                                                        <ExpandLess
                                                            sx={{
                                                                marginLeft:
                                                                    "auto",
                                                                color: isDark
                                                                    ? "text.secondary"
                                                                    : "#94a3b8",
                                                            }}
                                                        />
                                                    ) : (
                                                        <ExpandMore
                                                            sx={{
                                                                marginLeft:
                                                                    "auto",
                                                                color: isDark
                                                                    ? "text.secondary"
                                                                    : "#94a3b8",
                                                            }}
                                                        />
                                                    ))}
                                            </ListItem>
                                        </Tooltip>

                                        <Collapse
                                            in={
                                                flashcardsMenuExpanded &&
                                                !minimized
                                            }
                                            timeout="auto"
                                            sx={{ ml: 1 }}
                                        >
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/flashcards"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <GenerateIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        "Generera flashcards"
                                                    }
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>
                                            <ListItem
                                                button
                                                onClick={() =>
                                                    setOpenFlashcardsBank(true)
                                                }
                                                sx={{
                                                    ...listItemStyles,
                                                    display: "none",
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <DashboardIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        "Flashcards-banken"
                                                    }
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>

                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/mina-flashcards"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <FolderOpenIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Mina flashcards"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>

                                            <Divider sx={{ mt: 2, mb: 2 }} />
                                        </Collapse>

                                        {/* Modify the Podcast menu item */}
                                        {!hiddenMenus.includes("podcast") && (
                                            <Tooltip
                                                title="Podcast"
                                                placement="right"
                                                disableHoverListener={
                                                    !minimized
                                                }
                                                disableFocusListener={
                                                    !minimized
                                                }
                                                disableTouchListener={
                                                    !minimized
                                                }
                                            >
                                                <ListItem
                                                    button
                                                    onClick={() => {
                                                        if (minimized) {
                                                            navigate(
                                                                "/app/podcast",
                                                            );
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        } else {
                                                            setPodcastMenuExpanded(
                                                                !podcastMenuExpanded,
                                                            );
                                                        }
                                                    }}
                                                    sx={{
                                                        ...listItemStyles,
                                                        marginBottom: "3px",
                                                        justifyContent:
                                                            minimized
                                                                ? "center"
                                                                : "flex-start",
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={listItemIconStyles}
                                                    >
                                                        <MicIcon />
                                                    </ListItemIcon>
                                                    {!minimized && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                flex: 1,
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    "Podcast"
                                                                }
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                        </Box>
                                                    )}
                                                    {!minimized &&
                                                        (podcastMenuExpanded ? (
                                                            <ExpandLess
                                                                sx={{
                                                                    marginLeft:
                                                                        "auto",
                                                                    color: isDark
                                                                        ? "text.secondary"
                                                                        : "#94a3b8",
                                                                }}
                                                            />
                                                        ) : (
                                                            <ExpandMore
                                                                sx={{
                                                                    marginLeft:
                                                                        "auto",
                                                                    color: isDark
                                                                        ? "text.secondary"
                                                                        : "#94a3b8",
                                                                }}
                                                            />
                                                        ))}
                                                </ListItem>
                                            </Tooltip>
                                        )}
                                        <Collapse
                                            in={
                                                podcastMenuExpanded &&
                                                !minimized
                                            }
                                            timeout="auto"
                                            sx={{ ml: 1 }}
                                        >
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/podcast"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <GenerateIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Generera podcast"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>

                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/mina-podcasts"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <FolderOpenIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Mina podcasts"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>

                                            <Divider sx={{ mt: 2, mb: 2 }} />
                                        </Collapse>

                                        {/* Modify the Bedömning menu item */}

                                        {/* Modify the Bilder menu item (for teachers) */}
                                        {accountType === "teacher" && (
                                            <Tooltip
                                                title="Bilder"
                                                placement="right"
                                                disableHoverListener={
                                                    !minimized
                                                }
                                                disableFocusListener={
                                                    !minimized
                                                }
                                                disableTouchListener={
                                                    !minimized
                                                }
                                            >
                                                <ListItem
                                                    button
                                                    onClick={() => {
                                                        if (minimized) {
                                                            navigate(
                                                                "/app/generera-bild",
                                                            );
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        } else {
                                                            setImagesMenuExpanded(
                                                                !imagesMenuExpanded,
                                                            );
                                                        }
                                                    }}
                                                    sx={{
                                                        ...listItemStyles,
                                                        marginBottom: "3px",
                                                        justifyContent:
                                                            minimized
                                                                ? "center"
                                                                : "flex-start",
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={listItemIconStyles}
                                                    >
                                                        <AddPhotoAlternateIcon />
                                                    </ListItemIcon>
                                                    {!minimized && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                flex: 1,
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    "Bilder"
                                                                }
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                        </Box>
                                                    )}
                                                    {!minimized &&
                                                        (imagesMenuExpanded ? (
                                                            <ExpandLess
                                                                sx={{
                                                                    marginLeft:
                                                                        "auto",
                                                                    color: "text.secondary",
                                                                }}
                                                            />
                                                        ) : (
                                                            <ExpandMore
                                                                sx={{
                                                                    marginLeft:
                                                                        "auto",
                                                                    color: isDark
                                                                        ? "text.secondary"
                                                                        : "#94a3b8",
                                                                }}
                                                            />
                                                        ))}
                                                </ListItem>
                                            </Tooltip>
                                        )}
                                        <Collapse
                                            in={
                                                imagesMenuExpanded && !minimized
                                            }
                                            timeout="auto"
                                            sx={{ ml: 1 }}
                                        >
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/generera-bild"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <GenerateIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Generera bilder"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/vision"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <ImageSearchIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Bildanalys"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>
                                            <ListItem
                                                button
                                                onClick={() =>
                                                    setOpenImageBank(true)
                                                }
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <PhotoLibraryIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Bildbanken"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>
                                            <Divider sx={{ mt: 2, mb: 2 }} />
                                        </Collapse>

                                        {/* Modify the Assistenter menu item */}
                                        {!hiddenMenus.includes(
                                            "assistenter",
                                        ) && (
                                            <Tooltip
                                                title="Assistenter"
                                                placement="right"
                                                disableHoverListener={
                                                    !minimized
                                                }
                                                disableFocusListener={
                                                    !minimized
                                                }
                                                disableTouchListener={
                                                    !minimized
                                                }
                                            >
                                                <ListItem
                                                    button
                                                    onClick={() => {
                                                        if (minimized) {
                                                            navigate(
                                                                "/app/larare",
                                                            );
                                                            if (isMobile)
                                                                toggleSidebar();
                                                        } else {
                                                            setChatsExpanded(
                                                                !chatsExpanded,
                                                            );
                                                        }
                                                    }}
                                                    sx={{
                                                        ...listItemStyles,
                                                        marginBottom: "3px",
                                                        justifyContent:
                                                            minimized
                                                                ? "center"
                                                                : "flex-start",
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={listItemIconStyles}
                                                    >
                                                        <SmartToyIcon />
                                                    </ListItemIcon>
                                                    {!minimized && (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                flex: 1,
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    "Assistenter"
                                                                }
                                                                sx={
                                                                    listItemTextStyles
                                                                }
                                                            />
                                                        </Box>
                                                    )}
                                                    {!minimized &&
                                                        (chatsExpanded ? (
                                                            <ExpandLess
                                                                sx={{
                                                                    marginLeft:
                                                                        "auto",
                                                                    color: isDark
                                                                        ? "text.secondary"
                                                                        : "#94a3b8",
                                                                }}
                                                            />
                                                        ) : (
                                                            <ExpandMore
                                                                sx={{
                                                                    marginLeft:
                                                                        "auto",
                                                                    color: isDark
                                                                        ? "text.secondary"
                                                                        : "#94a3b8",
                                                                }}
                                                            />
                                                        ))}
                                                </ListItem>
                                            </Tooltip>
                                        )}
                                        <Collapse
                                            in={chatsExpanded && !minimized}
                                            timeout="auto"
                                            sx={{ ml: 1 }}
                                        >
                                            <TeacherList
                                                teachers={teachers}
                                                handleTeacherClick={
                                                    handleTeacherClick
                                                }
                                                onDeleteTeacher={
                                                    handleDeleteTeacher
                                                }
                                                onAddTeacher={onAddTeacher}
                                                isDark={isDark}
                                                user={user}
                                                onPublicTeachers={
                                                    onPublicTeachers
                                                }
                                                toggleSidebar={toggleSidebar}
                                                isMobile={isMobile}
                                            />
                                        </Collapse>

                                        <Divider
                                            sx={{
                                                mt: 2,
                                                mb: 2,
                                                display:
                                                    accountType === "student"
                                                        ? "none"
                                                        : "none",
                                            }}
                                        />

                                        <Tooltip
                                            title="Hjälpmedel"
                                            placement="right"
                                            disableHoverListener={!minimized}
                                            disableFocusListener={!minimized}
                                            disableTouchListener={!minimized}
                                        >
                                            <ListItem
                                                button
                                                onClick={() => {
                                                    if (minimized) {
                                                        navigate(
                                                            "/app/textlabbet",
                                                        );
                                                        if (isMobile)
                                                            toggleSidebar();
                                                    } else {
                                                        setHjalpmedelMenuExpanded(
                                                            !hjalpmedelMenuExpanded,
                                                        );
                                                    }
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                    marginBottom: "3px",
                                                    justifyContent: minimized
                                                        ? "center"
                                                        : "flex-start",
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <ConstructionIcon />
                                                </ListItemIcon>
                                                {!minimized && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            flex: 1,
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                "Hjälpmedel"
                                                            }
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                                {!minimized &&
                                                    (hjalpmedelMenuExpanded ? (
                                                        <ExpandLess
                                                            sx={{
                                                                marginLeft:
                                                                    "auto",
                                                                color: isDark
                                                                    ? "text.secondary"
                                                                    : "#94a3b8",
                                                            }}
                                                        />
                                                    ) : (
                                                        <ExpandMore
                                                            sx={{
                                                                marginLeft:
                                                                    "auto",
                                                                color: isDark
                                                                    ? "text.secondary"
                                                                    : "#94a3b8",
                                                            }}
                                                        />
                                                    ))}
                                            </ListItem>
                                        </Tooltip>

                                        <Collapse
                                            in={
                                                hjalpmedelMenuExpanded &&
                                                !minimized
                                            }
                                            timeout="auto"
                                            sx={{ ml: 1 }}
                                        >
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/vision"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <ImageSearchIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Bildanalys"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>

                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/textlabbet"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <EditIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={"Textlabbet"}
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>
                                            <ListItem
                                                button
                                                component={Link}
                                                to="/app/kurser"
                                                onClick={() => {
                                                    if (isMobile)
                                                        toggleSidebar();
                                                }}
                                                sx={{
                                                    ...listItemStyles,
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={listItemIconStyles}
                                                >
                                                    <MenuOpenIcon />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary="Skapa kurs"
                                                    sx={listItemTextStyles}
                                                />
                                            </ListItem>
                                            {accountType === "teacher" && (
                                                <ListItem
                                                    button
                                                    component={Link}
                                                    to="/app/generera-powerpoint"
                                                    onClick={() => {
                                                        if (isMobile)
                                                            toggleSidebar();
                                                    }}
                                                    sx={{
                                                        ...listItemStyles,
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={listItemIconStyles}
                                                    >
                                                        <PowerPointIcon />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary="Generera PowerPoint"
                                                        sx={listItemTextStyles}
                                                    />
                                                </ListItem>
                                            )}
                                            <Divider sx={{ mt: 2, mb: 2 }} />
                                        </Collapse>

                                        {accountType === "student" && (
                                            <Tooltip
                                                title={
                                                    accountType === "teacher"
                                                        ? "Prov"
                                                        : "Högskoleprovet"
                                                }
                                                placement="right"
                                                disableHoverListener={
                                                    !minimized
                                                }
                                                disableFocusListener={
                                                    !minimized
                                                }
                                                disableTouchListener={
                                                    !minimized
                                                }
                                            >
                                                <ListItem
                                                    button
                                                    component={Link}
                                                    to={
                                                        accountType ===
                                                        "teacher"
                                                            ? "/app/hp"
                                                            : "/app/hogskoleprov"
                                                    }
                                                    onClick={() => {
                                                        if (isMobile)
                                                            toggleSidebar();
                                                    }}
                                                    sx={{
                                                        ...listItemStyles,
                                                        justifyContent:
                                                            minimized
                                                                ? "center"
                                                                : "flex-start",
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={listItemIconStyles}
                                                    >
                                                        <HistoryEduIcon />
                                                    </ListItemIcon>
                                                    {!minimized && (
                                                        <ListItemText
                                                            primary={
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "space-between",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    <Typography>
                                                                        {accountType ===
                                                                        "teacher"
                                                                            ? "Prov"
                                                                            : "Högskoleprovet"}
                                                                    </Typography>
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            backgroundColor:
                                                                                "#c34340",
                                                                            color: "white",
                                                                            fontSize:
                                                                                "0.66rem",
                                                                            fontWeight:
                                                                                "600",
                                                                            p: 0.8,
                                                                            pl: 1.5,
                                                                            pr: 1.5,
                                                                            borderRadius:
                                                                                "9px",
                                                                            display:
                                                                                "none",
                                                                            lineHeight: 1,
                                                                            marginLeft:
                                                                                "auto",
                                                                        }}
                                                                    >
                                                                        NYTT
                                                                    </Box>
                                                                </Box>
                                                            }
                                                            sx={
                                                                listItemTextStyles
                                                            }
                                                        />
                                                    )}
                                                </ListItem>
                                            </Tooltip>
                                        )}
                                        <ListItem
                                            button
                                            component={Link}
                                            to="/app/rum"
                                            onClick={() => {
                                                if (isMobile) toggleSidebar();
                                            }}
                                            sx={{
                                                ...listItemStyles,

                                                display:
                                                    accountType === "student"
                                                        ? "none"
                                                        : "none",
                                                backgroundColor: "#8b5cf6",
                                                "& .MuiListItemIcon-root": {
                                                    color: "#fff",
                                                },
                                                "& .MuiListItemText-root .MuiTypography-root":
                                                    {
                                                        color: "#fff",
                                                    },
                                                "&:hover": {
                                                    ...listItemStyles[
                                                        "&:hover"
                                                    ],
                                                    backgroundColor: "#8b5cf6",
                                                    "& .MuiListItemIcon-root": {
                                                        color: "#fff",
                                                        background: "none",
                                                    },
                                                    "& .MuiListItemText-root .MuiTypography-root":
                                                        {
                                                            color: "#fff",
                                                        },
                                                },
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={listItemIconStyles}
                                            >
                                                <RocketLaunchIcon />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={"Uppgradera konto"}
                                                sx={listItemTextStyles}
                                            />
                                            <ChevronRightIcon
                                                className="chevron-icon"
                                                sx={chevronIcon}
                                            />
                                        </ListItem>
                                    </List>
                                </Box>
                            </>
                        )}
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 103, // Adjust this value to leave space for the user profile box below
                            left: 0,
                            right: 0,
                            alignItems: "center",
                            padding: "10px",
                            //display: 'flex',
                            display: "none",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            width: "100%",
                            borderTop: "1px solid",
                            borderColor: (theme) => theme.palette.divider,
                            backgroundColor: isDark ? undefined : "#f4faeb",

                            "&:hover": {
                                cursor: "pointer",
                                backgroundColor: isDark ? "#22303c" : "#eee9ff",
                            },
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: "-21px",
                                left: 0,
                                right: 0,
                                height: "20px",
                                background: isDark
                                    ? "linear-gradient(to bottom, transparent, #19242fba)"
                                    : "linear-gradient(to bottom, transparent, #ffffff)",
                                pointerEvents: "none",
                            },
                        }}
                        onClick={openFeedbackDialog}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                marginLeft: "13px",
                            }}
                        >
                            <FavoriteIcon
                                sx={{
                                    marginRight: 2,
                                    color: isDark ? undefined : "#14a561",
                                }}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: "500" }}
                            >
                                Ge feedback
                            </Typography>
                        </Box>
                    </Box>

                    {user && (
                        <>
                            {/* Tokens Tracker */}
                            <Box
                                sx={{
                                    display: "none",
                                    position: "absolute",
                                    bottom: 55,
                                    left: 0,
                                    right: 0,
                                    padding: "10px 15px",
                                    backgroundColor: (theme) =>
                                        theme.palette.background.paper,
                                    borderTop: "1px solid",
                                    borderColor: (theme) =>
                                        theme.palette.divider,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",

                                        marginBottom: "5px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <BoltIcon
                                            sx={{
                                                color: "text.secondary",
                                                fontSize: "1.2rem",
                                                mr: 0.3,

                                                opacity: "0.5",
                                            }}
                                        />

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: "12px",
                                                color: tokensExceeded
                                                    ? "#ff1744"
                                                    : (theme) =>
                                                          theme.palette.text
                                                              .secondary,
                                                marginRight: "4px", // Add some spacing between the text and icon
                                            }}
                                        >
                                            {tokens}/{maxTokens}
                                        </Typography>
                                    </Box>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={(tokens / maxTokens) * 100}
                                    sx={{
                                        height: 3,
                                        borderRadius: 15,
                                        backgroundColor: tokensExceeded
                                            ? "#ff1744"
                                            : isDark
                                              ? "#2a3b4c"
                                              : "#d4d4d8",
                                        "& .MuiLinearProgress-bar": {
                                            borderRadius: 2,
                                            backgroundColor: tokensExceeded
                                                ? "#ff1744"
                                                : isDark
                                                  ? "#14a561"
                                                  : "#14a561",
                                        },
                                    }}
                                />
                            </Box>

                            {/* Lesson Usage Tracker */}

                            {user.hejsan && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        bottom: 55,
                                        left: 0,
                                        right: 0,
                                        padding: "10px 15px",
                                        backgroundColor: (theme) =>
                                            theme.palette.background.paper,
                                        borderTop: "1px solid",
                                        borderColor: (theme) =>
                                            theme.palette.divider,
                                        "&:hover": {
                                            cursor: "pointer",
                                            backgroundColor: isDark
                                                ? "#22303c"
                                                : "#e9ffe99e",
                                        },
                                    }}
                                    onClick={() =>
                                        setLessonLimitDialogOpen(true)
                                    }
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginBottom: "5px",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        >
                                            <BoltIcon
                                                sx={{
                                                    color: "text.secondary",
                                                    fontSize: "1.2rem",
                                                    mr: 0.3,
                                                    opacity: "0.5",
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: "12px",
                                                    color: (theme) =>
                                                        theme.palette.text
                                                            .secondary,
                                                    marginRight: "4px",
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        color: lessonLimitExceeded
                                                            ? "#ff1744"
                                                            : isDark
                                                              ? "#12a561"
                                                              : "#12844f",
                                                    }}
                                                >
                                                    {lessonLimit - lessonCount}{" "}
                                                </Box>
                                                / {lessonLimit}
                                            </Typography>{" "}
                                        </Box>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={
                                            ((lessonLimit - lessonCount) /
                                                lessonLimit) *
                                            100
                                        }
                                        sx={{
                                            height: 3,
                                            borderRadius: 15,
                                            backgroundColor: lessonLimitExceeded
                                                ? "#ff1744"
                                                : isDark
                                                  ? "#2a3b4c"
                                                  : "#d4d4d8",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 2,
                                                backgroundColor:
                                                    lessonLimitExceeded
                                                        ? "#ff1744"
                                                        : isDark
                                                          ? "#14a561"
                                                          : "#14a561",
                                            },
                                        }}
                                    />
                                </Box>
                            )}

                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    alignItems: "center",
                                    padding: "10px",
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    backgroundColor: (theme) =>
                                        theme.palette.background.paper,
                                    borderTop: "1px solid",
                                    borderColor: (theme) =>
                                        theme.palette.divider,
                                    "&:hover": {
                                        cursor: "pointer",
                                        backgroundColor: isDark
                                            ? "#22303c"
                                            : "#eee9ff",
                                    },
                                }}
                                onClick={handleAvatarClick}
                            >
                                <Box
                                    sx={{
                                        borderRadius: "50%",
                                        padding: "2px",
                                        marginLeft: minimized ? "4px" : "13px",
                                        display: "inline-block",
                                        ...(user.isPremium && {
                                            border: "2px solid transparent",
                                            backgroundImage: isDark
                                                ? "linear-gradient(#1d2e3d, #1d2e3d), conic-gradient(#FF6600 0deg, #FF6600 90deg, #003366 90deg, #003366 180deg, #CC0000 180deg, #CC0000 270deg, #339933 270deg, #339933 360deg)"
                                                : "linear-gradient(white, white), conic-gradient(#FF6600 0deg, #FF6600 90deg, #003366 90deg, #003366 180deg, #CC0000 180deg, #CC0000 270deg, #339933 270deg, #339933 360deg)",
                                            backgroundOrigin: "border-box",
                                            backgroundClip:
                                                "padding-box, border-box",
                                        }),
                                    }}
                                >
                                    <Avatar
                                        src={user.photoURL}
                                        alt={user.displayName}
                                        sx={{
                                            height: "27px",
                                            width: "27px",
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        display: minimized ? "none" : "flex",
                                        alignItems: "center",
                                        flexGrow: 1,
                                        marginLeft: 2,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight:
                                                theme.palette.themeType ===
                                                "allakando"
                                                    ? "600"
                                                    : undefined,
                                        }}
                                    >
                                        {userDisplayName.length > 13
                                            ? userDisplayName.substring(0, 13) +
                                              "..."
                                            : userDisplayName}
                                    </Typography>
                                </Box>
                                {!minimized && (
                                    <IconButton
                                        size="small"
                                        sx={{ marginLeft: "auto" }}
                                    >
                                        <NavigateNextIcon />
                                    </IconButton>
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Drawer>
            <SettingsDialog
                open={open}
                onClose={() => setOpen(false)}
                onUserUpdate={updateUser}
                setOverrideTheme={setOverrideTheme}
            />

            <CreateClassDialog
                open={isCreateClassDialogOpen}
                onClose={() => setIsCreateClassDialogOpen(false)}
                onClassCreated={handleClassCreated}
                userId={user.uid}
            />

            <SettingsPopover
                user={user}
                isDark={isDark}
                openPopover={openPopover}
                anchorEl={anchorEl}
                handlePopoverClose={handlePopoverClose}
                handleLogout={handleLogout}
                openSettings={() => setOpen(true)}
                styles={styles}
                theme={theme}
                setOverrideTheme={setOverrideTheme}
            />

            <FeedbackDialog
                open={feedbackDialogOpen}
                onClose={() => setFeedbackDialogOpen(false)}
                user={user}
            />

            <QuizBank
                open={openQuizBank}
                onClose={() => setOpenQuizBank(false)}
                user={user}
                styles={styles}
                theme={theme}
            />

            <ImageBank
                open={openImageBank}
                onClose={() => setOpenImageBank(false)}
                user={user}
                styles={styles}
                theme={theme}
            />

            <FlashcardsBank
                open={openFlashcardsBank}
                onClose={() => setOpenFlashcardsBank(false)}
                user={user}
                styles={styles}
                theme={theme}
            />

            <LessonBankDialog
                open={openLessonBank}
                onClose={() => setOpenLessonBank(false)}
                user={user}
                styles={styles}
                theme={theme}
            />
            <LimitDialog
                open={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                }}
                userId={user.uid}
                user={user}
                accountType={accountType}
                styles={styles}
                titleText="Uppgradera ditt konto"
            />
            <LessonLimitDialog
                open={lessonLimitDialogOpen}
                onClose={() => setLessonLimitDialogOpen(false)}
                lessonCount={lessonCount}
                lessonLimit={lessonLimit}
                lessonLimitExceeded={lessonLimitExceeded}
                isDark={isDark}
                styles={styles}
                user={user}
            />
        </>
    );
};

const CustomIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 24 24">
        <path
            fill="currentColor"
            fillRule="evenodd"
            d="M8.857 3h6.286c1.084 0 1.958 0 2.666.058.729.06 1.369.185 1.961.487a5 5 0 0 1 2.185 2.185c.302.592.428 1.233.487 1.961.058.708.058 1.582.058 2.666v3.286c0 1.084 0 1.958-.058 2.666-.06.729-.185 1.369-.487 1.961a5 5 0 0 1-2.185 2.185c-.592.302-1.232.428-1.961.487C17.1 21 16.227 21 15.143 21H8.857c-1.084 0-1.958 0-2.666-.058-.728-.06-1.369-.185-1.96-.487a5 5 0 0 1-2.186-2.185c-.302-.592-.428-1.232-.487-1.961C1.5 15.6 1.5 14.727 1.5 13.643v-3.286c0-1.084 0-1.958.058-2.666.06-.728.185-1.369.487-1.96A5 5 0 0 1 4.23 3.544c.592-.302 1.233-.428 1.961-.487C6.9 3 7.773 3 8.857 3M6.354 5.051c-.605.05-.953.142-1.216.276a3 3 0 0 0-1.311 1.311c-.134.263-.226.611-.276 1.216-.05.617-.051 1.41-.051 2.546v3.2c0 1.137 0 1.929.051 2.546.05.605.142.953.276 1.216a3 3 0 0 0 1.311 1.311c.263.134.611.226 1.216.276.617.05 1.41.051 2.546.051h.6V5h-.6c-1.137 0-1.929 0-2.546.051M11.5 5v14h3.6c1.137 0 1.929 0 2.546-.051.605-.05.953-.142 1.216-.276a3 3 0 0 0 1.311-1.311c.134-.263.226-.611.276-1.216.05-.617.051-1.41.051-2.546v-3.2c0-1.137 0-1.929-.051-2.546-.05-.605-.142-.953-.276-1.216a3 3 0 0 0-1.311-1.311c-.263-.134-.611-.226-1.216-.276C17.029 5.001 16.236 5 15.1 5zM5 8.5a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1M5 12a1 1 0 0 1 1-1h1a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1"
            clipRule="evenodd"
        />
    </SvgIcon>
);

export default Sidebar;
